/**
 * EventTimeline — journal d'événements signalement en timeline humanisée (L13.16.1).
 *
 * Remplace l'ancien rendu « ligne de code » (`<pre>{JSON.stringify(payload)}</pre>`)
 * par une frise verticale conforme au mockup `mockup_admin_suivi_incident_et_notif`
 * (bloc « Étapes ») : dot par événement, icône typée, libellé en français clair,
 * détail lisible, et méta (auteur · date).
 *
 * Le payload brut de chaque événement est traduit en phrase française par
 * `humanize()` — aucun JSON n'est jamais affiché à l'utilisateur.
 *
 * Réutilisable : la fiche pro ET la future vue administré (L13.16.2) s'appuient
 * sur ce composant.
 *
 * Accessibilité : liste ordonnée `<ol>`, icônes décoratives (`aria-hidden`),
 * dates en toutes lettres lisibles par lecteur d'écran.
 */

import { clsx } from 'clsx';
import {
  FilePlus2, ArrowRightLeft, UserCheck, UserMinus, MessageSquare,
  ImageIcon, Bell, Circle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { SignalementEvenement } from '@roronoa-collectivites/shared-types';
import { SIGNALEMENT_STATUTS } from '@roronoa-collectivites/shared-types';

// ----------------------------------------------------------------------------
// Traduction statut value → libellé FR
// ----------------------------------------------------------------------------

function statutLabel(value: unknown): string {
  if (typeof value !== 'string') return '—';
  return SIGNALEMENT_STATUTS.find((s) => s.value === value)?.label ?? value;
}

// ----------------------------------------------------------------------------
// Humanisation d'un événement : payload brut → contenu lisible
// ----------------------------------------------------------------------------

interface HumanEvent {
  icon: LucideIcon;
  title: string;
  /** Détail en clair (sans guillemets) — ex. « De Reçu à Planifié ». */
  detail?: string;
  /** Texte libre saisi par un utilisateur (commentaire) — rendu entre guillemets. */
  quote?: string;
}

/**
 * Traduit un événement métier en contenu d'affichage.
 * `resolveUserName` permet de remplacer un UUID d'agent par son nom (optionnel).
 */
function humanize(
  evt: SignalementEvenement,
  resolveUserName?: (userId: string) => string | undefined,
): HumanEvent {
  const p = evt.payload ?? {};

  switch (evt.type) {
    case 'creation': {
      if (p.source === 'public_form') {
        return {
          icon: FilePlus2,
          title: 'Signalement créé',
          detail: 'Déposé via le formulaire public de la commune',
        };
      }
      return {
        icon: FilePlus2,
        title: 'Signalement créé',
        detail: typeof p.numero === 'string' ? `Fiche ${p.numero}` : undefined,
      };
    }

    case 'statut_change': {
      const from = statutLabel(p.from);
      const to = statutLabel(p.to);
      return {
        icon: ArrowRightLeft,
        title: 'Statut mis à jour',
        detail: `De « ${from} » à « ${to} »`,
        quote: typeof p.commentaire === 'string' && p.commentaire.trim()
          ? p.commentaire.trim()
          : undefined,
      };
    }

    case 'assignation': {
      const userId = typeof p.assigne_user_id === 'string' ? p.assigne_user_id : null;
      if (!userId) {
        return { icon: UserMinus, title: 'Affectation retirée' };
      }
      const name = resolveUserName?.(userId);
      return {
        icon: UserCheck,
        title: 'Affectation',
        detail: name ? `Affecté à ${name}` : 'Affecté à un agent',
      };
    }

    case 'commentaire': {
      return {
        icon: MessageSquare,
        title: 'Commentaire ajouté',
        quote: typeof p.commentaire === 'string' ? p.commentaire.trim() : undefined,
      };
    }

    case 'photo_ajoutee': {
      const kind = p.kind;
      const kindLabel =
        kind === 'agent_avant' ? 'Photo « avant » ajoutée'
        : kind === 'agent_apres' ? 'Photo « après » ajoutée'
        : 'Photo ajoutée';
      return { icon: ImageIcon, title: kindLabel };
    }

    case 'notif_envoyee': {
      return {
        icon: Bell,
        title: 'Notification envoyée',
        detail: typeof p.canal === 'string' ? `Canal : ${p.canal}` : 'L\'administré a été prévenu',
      };
    }

    default:
      return { icon: Circle, title: evt.type };
  }
}

// ----------------------------------------------------------------------------
// Format de date FR lisible
// ----------------------------------------------------------------------------

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
    + ' · '
    + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

// ----------------------------------------------------------------------------
// Composant
// ----------------------------------------------------------------------------

interface EventTimelineProps {
  /** Événements, triés du plus ancien au plus récent (ordre de l'API). */
  events: SignalementEvenement[];
  /** Résout un UUID utilisateur en nom affichable (pour les affectations). */
  resolveUserName?: (userId: string) => string | undefined;
  className?: string;
}

export function EventTimeline({ events, resolveUserName, className }: EventTimelineProps) {
  if (events.length === 0) return null;

  return (
    <ol className={clsx('space-y-0', className)}>
      {events.map((evt, i) => {
        const isLast = i === events.length - 1;
        const h = humanize(evt, resolveUserName);
        const Icon = h.icon;

        return (
          <li key={evt.id} className="flex gap-3">
            {/* Colonne dot + ligne de liaison */}
            <div className="flex flex-col items-center">
              <span
                aria-hidden="true"
                className={clsx(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border',
                  // dernier événement = état courant (accent tech) ; les autres = faits (vert)
                  isLast
                    ? 'border-tech/40 bg-tech-tint/60 text-tech-fg'
                    : 'border-success/30 bg-success-tint/40 text-success',
                )}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
              </span>
              {!isLast && <span aria-hidden="true" className="my-1 w-px flex-1 bg-border" />}
            </div>

            {/* Contenu */}
            <div className={clsx('min-w-0 flex-1', !isLast && 'pb-4')}>
              <p
                className={clsx(
                  'text-sm font-medium',
                  isLast ? 'text-tech-fg' : 'text-ink',
                )}
              >
                {h.title}
              </p>

              {h.detail && (
                <p className="mt-0.5 text-sm text-ink-muted">{h.detail}</p>
              )}

              {h.quote && (
                <p className="mt-1.5 rounded-r-md border-l-2 border-border bg-surface/40 px-2.5 py-1.5 text-sm italic text-ink-muted">
                  «&nbsp;{h.quote}&nbsp;»
                </p>
              )}

              <p className="mt-1 text-xs text-ink-subtle">
                {evt.auteurNom ?? 'Système'} · {formatDate(evt.createdAt)}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
